import React, { FC, useState, useCallback, useMemo } from 'react';
import { trackEvent } from '../../lib/analytics';
import { Box, Stepper, Step, StepLabel, Alert, Snackbar } from '@mui/material';
import SwipeableViews from 'react-swipeable-views';
import { useBusinessSelection } from '../../hooks/useBusinessSelection';
import { RegistrationProvider } from '../../contexts/RegistrationContext';
import { OnboardingIntro } from '../../components/onboarding/OnboardingIntro';
import { OnboardingWelcome } from '../../components/onboarding/OnboardingWelcome';
import { BusinessSelectionForm } from '../../components/forms/BusinessSelectionForm';
import { DepartmentSelectionForm } from '../../components/forms/DepartmentSelectionForm';
import { PersonalInfoForm } from '../../components/forms/PersonalInfoForm';
import { HealthInfoForm } from '../../components/forms/HealthInfoForm';
import { TermsAcceptanceForm } from '../../components/forms/TermsAcceptanceForm';
import { useRegistrationContext } from '../../contexts/RegistrationContext';
import { useNavigation } from '../../hooks/useNavigation';

// ✅ CONSTANTES CENTRALIZADAS
const STEP_LABELS = [
  'Info. Personal',
  'Info. de Salud',
  'Términos de uso',
] as const;

// ✅ INTERFACE PARA STEP CONFIG
interface StepConfig {
  key: string;
  component: React.ReactElement;
  requiresBusiness: boolean;
}

const RegisterScreenContent: FC = () => {
  const { goToSuccess } = useNavigation();
  const [activeStep, setActiveStep] = useState(0);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const { showBusinessStep, business } = useBusinessSelection();
  const { submitRegistration, isFormValid, submitError, formData } =
    useRegistrationContext();

  // ✅ MANEJO DE STEPS MEJORADO
  const handleNext = useCallback(() => {
    setActiveStep(prevActiveStep => {
      const nextStep = prevActiveStep + 1;
      trackEvent('registration_step', 'conversion', {
        step: nextStep,
        step_name: STEP_LABELS[nextStep] ?? String(nextStep),
      });
      return nextStep;
    });
  }, []);

  const handleBack = useCallback(() => {
    setActiveStep(prevActiveStep => prevActiveStep - 1);
  }, []);

  const handleSkip = useCallback(() => {
    setActiveStep(1);
  }, []);

  // ✅ SUBMIT REAL IMPLEMENTADO - TODOs COMPLETADOS
  const handleSubmit = useCallback(async () => {
    if (!isFormValid) {
      setSnackbar({
        open: true,
        message: 'Por favor, complete todos los campos requeridos',
        severity: 'error',
      });
      return;
    }

    try {
      // ✅ Obtener valores del contexto de formulario
      // ✅ Usar businessId del formData o del Redux como fallback
      const businessId = formData.businessId || business;
      const activationCode = formData.activationCode;
      const termId = 1; // TODO: Debe venir del contexto de términos

      console.log('🔍 Form data at submit:', formData);
      console.log('🔍 BusinessId from formData:', formData.businessId);
      console.log('🔍 BusinessId from Redux:', business);
      console.log('🔍 Final BusinessId:', businessId);
      console.log('🔍 ActivationCode:', activationCode);

      // ✅ Validar que tenemos los datos necesarios
      if (!businessId || !activationCode) {
        console.log('❌ Missing businessId or activationCode');
        setSnackbar({
          open: true,
          message: 'Por favor, seleccione una empresa y departamento',
          severity: 'error',
        });
        return;
      }

      const result = await submitRegistration(
        businessId,
        activationCode,
        termId
      );

      if (result.success) {
        trackEvent('registration_complete', 'conversion', {
          business_id: businessId,
        });
        setSnackbar({
          open: true,
          message: 'Registro exitoso! Redirigiendo...',
          severity: 'success',
        });

        // ✅ Redirección implementada usando el hook
        setTimeout(() => {
          goToSuccess();
        }, 2000);
      } else {
        trackEvent('registration_error', 'error', {
          error: result.error || 'Error en el registro',
          step: activeStep,
        });
        setSnackbar({
          open: true,
          message: result.error || 'Error en el registro',
          severity: 'error',
        });
      }
    } catch (error) {
      trackEvent('registration_error', 'error', {
        error: 'Error inesperado',
        step: activeStep,
      });
      setSnackbar({
        open: true,
        message: 'Error inesperado. Por favor, intente nuevamente.',
        severity: 'error',
      });
    }
  }, [isFormValid, submitRegistration, formData, business, goToSuccess]);

  // ✅ CERRAR SNACKBAR
  const handleCloseSnackbar = useCallback(() => {
    setSnackbar(prev => ({ ...prev, open: false }));
  }, []);

  // ✅ CONFIGURACIÓN DE STEPS DINÁMICA
  const stepConfigs: StepConfig[] = useMemo(
    () => [
      {
        key: 'intro',
        component: (
          <OnboardingIntro
            key="intro"
            onSkip={handleSkip}
            onNext={handleNext}
          />
        ),
        requiresBusiness: false,
      },
      {
        key: 'welcome',
        component: <OnboardingWelcome key="welcome" onNext={handleNext} />,
        requiresBusiness: false,
      },
      ...(showBusinessStep
        ? [
            {
              key: 'business',
              component: (
                <BusinessSelectionForm
                  key="business"
                  onNext={handleNext}
                  onBack={handleBack}
                />
              ),
              requiresBusiness: true,
            },
          ]
        : []),
      {
        key: 'department',
        component: (
          <DepartmentSelectionForm
            key="department"
            onNext={handleNext}
            onBack={handleBack}
          />
        ),
        requiresBusiness: true,
      },
      {
        key: 'personal',
        component: (
          <PersonalInfoForm
            key="personal"
            onNext={handleNext}
            onBack={handleBack}
          />
        ),
        requiresBusiness: true,
      },
      {
        key: 'health',
        component: (
          <HealthInfoForm
            key="health"
            onNext={handleNext}
            onBack={handleBack}
          />
        ),
        requiresBusiness: true,
      },
      {
        key: 'terms',
        component: (
          <TermsAcceptanceForm
            key="terms"
            onBack={handleBack}
            onSubmit={handleSubmit}
          />
        ),
        requiresBusiness: true,
      },
    ],
    [showBusinessStep, handleNext, handleBack, handleSkip, handleSubmit]
  );

  // ✅ CÁLCULO DE OFFSET DEL STEPPER
  const stepperOffset = useMemo(() => {
    return showBusinessStep ? 4 : 3;
  }, [showBusinessStep]);

  // ✅ FILTRAR STEPS QUE REQUIEREN BUSINESS
  const businessSteps = useMemo(() => {
    return stepConfigs.filter(step => step.requiresBusiness);
  }, [stepConfigs]);

  // ✅ MOSTRAR STEPPER SOLO CUANDO SEA APROPIADO
  const shouldShowStepper = useMemo(() => {
    return activeStep > stepperOffset - 1 && businessSteps.length > 0;
  }, [activeStep, stepperOffset, businessSteps.length]);

  return (
    <Box sx={{ maxWidth: '100vw', minWidth: '310px', display: 'inline-block' }}>
      {/* ✅ STEPPER CONDICIONAL */}
      {shouldShowStepper && (
        <Box sx={{ width: '101%', marginY: '16px' }}>
          <Stepper activeStep={activeStep - stepperOffset} alternativeLabel>
            {STEP_LABELS.map(label => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>
      )}

      {/* ✅ VISTAS CON SWIPE DISABLED */}
      <SwipeableViews disabled index={activeStep}>
        {stepConfigs.map(step => step.component)}
      </SwipeableViews>

      {/* ✅ SNACKBAR PARA NOTIFICACIONES */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* ✅ MOSTRAR ERRORES DEL FORMULARIO */}
      {submitError && (
        <Snackbar
          open={!!submitError}
          autoHideDuration={6000}
          onClose={() => {
            // El error se limpia automáticamente en el hook, no necesitamos hacer nada aquí
          }}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert severity="error" sx={{ width: '100%' }}>
            {submitError}
          </Alert>
        </Snackbar>
      )}
    </Box>
  );
};

export const RegisterScreen: FC = () => {
  return (
    <RegistrationProvider>
      <RegisterScreenContent />
    </RegistrationProvider>
  );
};
